/**
 * First-order visible-light microscope calculations. Distances use millimetres,
 * micrometres, and nanometres exactly as named; magnification is unitless.
 */

export type ObjectivePower = 4 | 10 | 40 | 100;

export interface ObjectiveSpecification {
  magnification: ObjectivePower;
  numericalAperture: number;
  immersionRequired: boolean;
}

export const OBJECTIVES: Record<ObjectivePower, ObjectiveSpecification> = {
  4: { magnification: 4, numericalAperture: 0.10, immersionRequired: false },
  10: { magnification: 10, numericalAperture: 0.25, immersionRequired: false },
  40: { magnification: 40, numericalAperture: 0.65, immersionRequired: false },
  100: { magnification: 100, numericalAperture: 1.25, immersionRequired: true },
};

function assertPositive(name: string, value: number): void {
  if (!Number.isFinite(value) || value <= 0) {
    throw new RangeError(`${name} must be a finite, positive number`);
  }
}

/** Calculate total magnification from eyepiece and objective powers. */
export function calculateTotalMagnification(eyepiecePower: number, objectivePower: number): number {
  assertPositive('eyepiecePower', eyepiecePower);
  assertPositive('objectivePower', objectivePower);
  return eyepiecePower * objectivePower;
}

/**
 * Estimate field-of-view diameter at the specimen plane. Field number and
 * result are in mm; the estimate is independent of the eyepiece power because
 * the field number already describes the eyepiece's apparent field.
 */
export function calculateFieldOfViewDiameterMm(
  eyepieceFieldNumberMm: number,
  objectivePower: number,
): number {
  assertPositive('eyepieceFieldNumberMm', eyepieceFieldNumberMm);
  assertPositive('objectivePower', objectivePower);
  return eyepieceFieldNumberMm / objectivePower;
}

/** Abbe lateral resolution estimate: d = 0.61 λ / NA, returned in µm. */
export function calculateResolutionLimitUm(
  numericalAperture: number,
  wavelengthNm = 550,
): number {
  assertPositive('numericalAperture', numericalAperture);
  assertPositive('wavelengthNm', wavelengthNm);
  return (0.61 * (wavelengthNm / 1000)) / numericalAperture;
}

/**
 * Return a simple useful-magnification range based on resolution. Values well
 * above this range enlarge the image without revealing additional detail.
 */
export function calculateUsefulMagnificationRange(
  numericalAperture: number,
  resolutionLimitUm = calculateResolutionLimitUm(numericalAperture),
): { minimum: number; maximum: number } {
  assertPositive('numericalAperture', numericalAperture);
  assertPositive('resolutionLimitUm', resolutionLimitUm);
  // A relaxed range of 500–1000× numerical aperture is commonly used for
  // teaching instruments; this is guidance, not a hard optical cutoff.
  return { minimum: 500 * numericalAperture, maximum: 1000 * numericalAperture };
}
